"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getFundingRequestGeneratorWithLoanMechanics = exports.getDrawRequest = void 0;
const lodash_1 = __importDefault(require("lodash"));
const common_1 = require("@finley-technologies/common");
const finley_types_1 = require("@finley-technologies/finley-types");
const accrual_1 = require("./accrual");
const dates_1 = require("./dates");
const calculateTotal = (amounts) => common_1.utils.jsRound({
    value: amounts.reduce((sum, amount) => sum + amount, 0),
    precision: 2,
});
const getLoanPricing = ({ pricingOption, commitment, benchmarkRates, targetFundingDate, }) => {
    const lookbackDays = pricingOption.type === 'benchmark'
        ? pricingOption.determination.lookback.days
        : 0;
    const determinationDate = common_1.date.addBusinessDays(targetFundingDate, -1 * lookbackDays);
    const benchmarkName = pricingOption.type === 'benchmark' ? pricingOption.benchmark : '';
    const publishedRates = pricingOption.type === 'benchmark'
        ? benchmarkRates[pricingOption.benchmark] ?? []
        : [];
    const resolvedRate = publishedRates.find((rate, idx) => {
        // Automatic selection if date matches exactly
        if (rate.effectiveDate === determinationDate) {
            return true;
        }
        // Otherwise, there may be a mismatch in holiday schedules or lack of published rate availability
        // Proceed by checking against next published date to provide the latest available rate prior to determination date as fallback
        const nextRate = publishedRates[idx + 1];
        if (nextRate) {
            // If next rate is past determination date, return
            if (nextRate.effectiveDate > determinationDate) {
                return true;
            }
            return false;
        }
        // This is the last rate, automatically select for use
        return true;
    })?.rate ?? 0;
    const benchmarkRate = resolvedRate;
    const margin = pricingOption?.margin.value ?? 0;
    const allInRate = benchmarkRate + margin;
    // TODO: @sachinr4 to update skim fee lookups later as part of CA structure changes
    // For now, these slugs are hardcoded because only BLITA orgs require a service fee
    const serviceFee = commitment.loans.some((currLoan) => currLoan.lenders.some((lender) => ['bankLeumiIsraelTelAviv', 'bankLeumiLe'].includes(lender.entity.slug)))
        ? 0.0025
        : 0;
    return {
        benchmark: {
            name: benchmarkName,
            determinationDate,
            rate: benchmarkRate,
        },
        waApplicableMarginRate: margin,
        allInRate,
        serviceFee,
    };
};
const getAllocatedShares = ({ commitmentAmount, requestAmount, lenders, targetFundingDate, maturityDate, pricing, borrowerBankAccount, lenderBankAccounts, fundingRequestComponents, readOnly, }) => {
    let remainingAmount = requestAmount;
    const allocatedShares = lenders.map((lender) => {
        // Match existing component first, then assign amount depending on mode (read-only)
        const bankAccount = lenderBankAccounts.find((account) => account.organizationId === lender.entity.id);
        // May need to monitor and revisit if we have multiple requests and
        // components associated with the same "lender" for a loan
        const fundingRequestComponent = fundingRequestComponents.find((item) => item.type === 'drawRequest' &&
            item.sourceBankAccountId === bankAccount?.id);
        (0, common_1.finleyAssert)(fundingRequestComponent, `Expected to find funding component when updating draw request amount`);
        const proRataAmount = common_1.utils.jsRound({
            value: common_1.utils.divideByOrZero(lender.amount, commitmentAmount) *
                requestAmount,
            precision: 2,
        });
        const lenderAllocation = Math.min(proRataAmount, remainingAmount);
        remainingAmount = common_1.utils.jsRound({
            value: remainingAmount - lenderAllocation,
            precision: 2,
        });
        const shareAmount = readOnly
            ? fundingRequestComponent.amount
            : lenderAllocation;
        return {
            fundingRequestComponentId: fundingRequestComponent.id,
            lender: { organizationId: lender.entity.id, name: lender.entity.name },
            bankAccounts: {
                source: bankAccount?.name ?? '',
                destination: borrowerBankAccount?.name ?? '',
            },
            percent: common_1.utils.divideByOrZero(shareAmount, requestAmount),
            amount: shareAmount,
            applicableMargin: pricing.applicableMargin,
            projectedInterest: (0, accrual_1.calculateProjectedInterest)({
                balance: shareAmount,
                dayCountConvention: 'Actual / 360',
                targetFundingDate,
                maturityDate,
                allInRate: pricing.allInRate,
            }),
        };
    });
    // TODO: more advance checks, this is sufficient for now
    // Defaulting to sending all remaining to the 1st lender share in the list (implicit expectation in configuration that this would be the lead lender and/or agent)
    // The remaining amount should always come out to less than the rounding tolerance for the group (1 penny * number of shares)
    if (remainingAmount > 0 && allocatedShares[0]) {
        allocatedShares[0].amount = common_1.utils.jsRound({
            value: allocatedShares[0].amount + remainingAmount,
            precision: 2,
        });
    }
    return allocatedShares;
};
const getDrawRequest = ({ drawRequest, pricingOption, loanMechanics, benchmarkRates, borrowerBankAccount, lenderBankAccounts, fundingRequestComponents, updateParams, }) => {
    (0, common_1.finleyAssert)(drawRequest.loan, 'Loan attributes must be present on draw request');
    const commitment = loanMechanics.commitments.find((item) => item.key === drawRequest.key);
    (0, common_1.finleyAssert)(commitment, 'Commitment must be present');
    const loanConfiguration = loanMechanics.loans.find((item) => item.pricingOptionKey === pricingOption.key);
    (0, common_1.finleyAssert)(loanConfiguration, 'Global loan configuration must be present');
    const loan = commitment.loans.find((item) => item.loanKey === loanConfiguration.key);
    (0, common_1.finleyAssert)(loan, 'Commitment loan configuration must be present');
    // Recalculate pricing if (1) pricing option has changed OR (2) target funding date has changed
    // Amount and maturity date changes only affect the projected interest for each share
    const pricing = drawRequest.pricingOption.key !== pricingOption.key ||
        drawRequest.targetFundingDate !== updateParams.targetFundingDate
        ? getLoanPricing({
            pricingOption,
            commitment,
            benchmarkRates,
            targetFundingDate: updateParams.targetFundingDate,
        })
        : drawRequest.loan.pricing;
    const shares = getAllocatedShares({
        commitmentAmount: commitment.limit.amount,
        requestAmount: updateParams.amount,
        lenders: loan.lenders,
        targetFundingDate: updateParams.targetFundingDate,
        maturityDate: updateParams.maturityDate,
        pricing: {
            applicableMargin: pricing.waApplicableMarginRate,
            allInRate: pricing.allInRate,
        },
        borrowerBankAccount,
        lenderBankAccounts,
        fundingRequestComponents,
        readOnly: false,
    });
    return {
        ...drawRequest,
        requestAmount: updateParams.amount,
        targetFundingDate: updateParams.targetFundingDate,
        pricingOption: { key: pricingOption.key, name: pricingOption.name },
        loan: {
            ...drawRequest.loan,
            externalLoanId: updateParams.externalLoanId ?? undefined,
            maturityDate: updateParams.maturityDate,
            pricing,
            shares,
        },
    };
};
exports.getDrawRequest = getDrawRequest;
const getFundingRequestGeneratorWithLoanMechanics = (params) => {
    (0, common_1.finleyAssert)(params.fundingMechanicsV2Serialized, `Funding mechanics must be provided for request`);
    const { fundingMechanicsV2Serialized, loanBalances, mode, allowedActions, loans, fundingRequestToDuplicate, } = params;
    const submittedLoans = loans ?? [];
    if (mode === 'upsize') {
        (0, common_1.finleyAssert)(submittedLoans.length === 1, `Upsizing request requires 1 loan, but found ${submittedLoans.length}`);
    }
    // Locally scoped components to avoid mutation at parent scope
    const currentFundingRequestComponents = lodash_1.default.cloneDeep(params.currentFundingRequestComponents);
    const fundingMechanics = JSON.parse(fundingMechanicsV2Serialized);
    const { loanMechanics } = fundingMechanics;
    const globalCommitment = loanMechanics.commitments.reduce((sum, { limit }) => (limit.sublimit ? sum : sum + limit.amount), 0);
    const totalOutstandingBalance = loanBalances.reduce((sum, { amount }) => sum + amount, 0);
    const commitmentBalances = new Map();
    for (const loanBalance of loanBalances) {
        const entry = commitmentBalances.get(loanBalance.commitmentKey) ?? 0;
        commitmentBalances.set(loanBalance.commitmentKey, entry + loanBalance.amount);
    }
    const globalUnused = Math.max(globalCommitment - totalOutstandingBalance, 0);
    const fundingRequestId = currentFundingRequestComponents[0].fundingRequestId;
    const borrowerBankAccount = fundingMechanics.bankAccounts.find((account) => account.organizationId === fundingMechanics.borrower.organizationId);
    (0, common_1.finleyAssert)(borrowerBankAccount, 'Borrower bank account must be present');
    (0, common_1.finleyAssert)(loanMechanics.pricingOptions.length > 0, 'Pricing options must be provided for funding request');
    const drawRequestComponent = currentFundingRequestComponents.find((component) => component.type === 'drawRequest');
    const initialTargetFundingDate = drawRequestComponent?.postingDate ??
        drawRequestComponent?.targetFundingDate ??
        (0, dates_1.generateFundingDate)({
            configuration: { offset: { count: 0, unit: 'businessDay' } },
        });
    const fundingRequestToDuplicatePricingOption = loanMechanics.pricingOptions.find((option) => option.key ===
        (fundingRequestToDuplicate?.resolvedLoans ?? [])[0]?.pricing.key);
    const fundingRequestToDuplicateInvoiceMechanicsKey = (fundingRequestToDuplicate?.resolvedLoans ?? [])[0]?.invoiceMechanics
        ?.key ?? null;
    const submittedLoanPricingOption = submittedLoans[0]
        ? loanMechanics.pricingOptions.find((option) => option.key === submittedLoans[0].pricing.key)
        : null;
    const submittedLoanInvoiceMechanicsKey = submittedLoans[0]
        ? submittedLoans[0].invoiceMechanics?.key
        : null;
    const initialPricingOption = fundingRequestToDuplicatePricingOption ??
        submittedLoanPricingOption ??
        loanMechanics.pricingOptions[0];
    const initialInvoiceMechanicsKey = fundingRequestToDuplicateInvoiceMechanicsKey ??
        submittedLoanInvoiceMechanicsKey ??
        undefined;
    const global = {
        externalLoanId: (fundingRequestToDuplicate?.resolvedLoans ?? [])[0]?.externalLoanId ??
            submittedLoans[0]?.externalLoanId,
        targetFundingDate: initialTargetFundingDate,
        maturityDate: (fundingRequestToDuplicate?.resolvedLoans ?? [])[0]?.dates.maturityDate ??
            submittedLoans[0]?.dates.maturityDate ??
            common_1.date.addMonths(initialTargetFundingDate, 1),
        pricingOption: initialPricingOption,
        invoiceMechanicsKey: initialInvoiceMechanicsKey,
    };
    // Match cases for initial setup
    const drawComponents = currentFundingRequestComponents.filter((component) => component.type === finley_types_1.FundingRequest.FundingRequestComponentType.DrawRequest);
    // Set up initial set of requests based on commitment and balance
    // After setup, then perform updates based on allowed actions and mode (set pricing/amounts if read only)
    const drawRequests = new Map();
    for (const commitment of loanMechanics.commitments) {
        // Maximum constraint: unused of commitment
        // Sublimit: minimum of global unused and sublimit
        // Example: global $100mm, C1 $50mm, C2 $50mm, C3 sublimit $30mm
        //          -> draw up to $30mm on C3, as long as global unused is higher
        const commitmentUnused = commitment.limit.amount - (commitmentBalances.get(commitment.key) ?? 0);
        const maximum = Math.min(commitmentUnused, globalUnused);
        const loanConfiguration = loanMechanics.loans.find((item) => item.pricingOptionKey === global.pricingOption.key);
        (0, common_1.finleyAssert)(loanConfiguration, 'Global loan configuration must be present');
        const targetLoan = commitment.loans.find((item) => item.loanKey === loanConfiguration.key);
        (0, common_1.finleyAssert)(targetLoan, 'Commitment loan configuration must be present');
        const loanPricing = getLoanPricing({
            commitment,
            pricingOption: global.pricingOption,
            benchmarkRates: fundingMechanics.benchmarkRates,
            targetFundingDate: global.targetFundingDate,
        });
        // This logic must stay in place to preserve historical request accuracy
        let loanRequestComponents;
        // TODO: fundingRequestToDuplicate stuff seems broken for multi-commitment structures
        if (fundingRequestToDuplicate) {
            // Find a matching loan, if one was submitted
            const matchingSubmittedLoan = (fundingRequestToDuplicate.resolvedLoans ?? []).find((loan) => loan.commitmentKey === commitment.key);
            loanRequestComponents = drawComponents.map((drawComponent) => {
                const matchingComponent = fundingRequestToDuplicate.fundingRequestComponents.find((component) => component.sourceBankAccountId === drawComponent.sourceBankAccountId);
                return {
                    ...drawComponent,
                    amount: matchingSubmittedLoan && matchingComponent
                        ? matchingComponent.amount
                        : drawComponent.amount,
                };
            });
        }
        else {
            // Find a matching loan, if one was submitted
            const matchingSubmittedLoan = submittedLoans.find((loan) => loan.commitmentKey === commitment.key);
            loanRequestComponents = drawComponents
                // Filter on components using commitment key, if set
                // Otherwise, all components are set on all requests
                // When no commitment key is available, only one loan will be submitted
                .filter((component) => !component.commitmentKey ||
                component.commitmentKey === commitment.key)
                // If a matching loan was found, set original component amount, otherwise 0
                // This is primarily to support requests for which commitment key was not set
                .map((component) => ({
                ...component,
                amount: matchingSubmittedLoan ? component.amount : 0,
            }));
        }
        const requestAmount = calculateTotal(loanRequestComponents.map((item) => item.amount));
        const drawRequest = {
            key: commitment.key,
            name: commitment.name,
            fxDetails: null,
            initialAmount: 0,
            requestAmount,
            targetFundingDate: global.targetFundingDate,
            constraints: {
                maximum,
                minimum: 0,
                increment: null,
            },
            attachmentLimit: 1,
            pricingOption: {
                key: global.pricingOption.key,
                name: global.pricingOption.name,
            },
            loan: {
                key: targetLoan.loanKey,
                externalLoanId: global.externalLoanId ?? undefined,
                maturityDate: global.maturityDate,
                pricing: loanPricing,
                invoicingMechanicsKey: global.invoiceMechanicsKey,
                shares: getAllocatedShares({
                    commitmentAmount: commitment.limit.amount,
                    requestAmount,
                    lenders: targetLoan.lenders,
                    targetFundingDate: global.targetFundingDate,
                    maturityDate: global.maturityDate,
                    pricing: {
                        applicableMargin: loanPricing.waApplicableMarginRate,
                        allInRate: loanPricing.allInRate,
                    },
                    borrowerBankAccount,
                    lenderBankAccounts: fundingMechanics.bankAccounts,
                    fundingRequestComponents: loanRequestComponents,
                    readOnly: allowedActions === 'readOnly',
                }),
            },
        };
        if (mode === 'upsize') {
            const loan = submittedLoans[0];
            if (loan && loan.commitmentKey !== commitment.key)
                continue;
        }
        drawRequests.set(commitment.key, drawRequest);
    }
    const expectedRequests = mode === 'upsize'
        ? [{ commitmentKey: submittedLoans[0].commitmentKey }]
        : loanMechanics.commitments.map((commitment) => ({
            commitmentKey: commitment.key,
        }));
    return {
        deficiency: 0,
        summary: {
            commitment: { amount: globalCommitment, currency: 'USD' },
            // can't use totalBorrowingBaseAmount because it's no longer accurate
            // db records for totalBorrowingBaseAmount need to be migrated before use @danitian
            borrowingBaseAmount: 0,
            maximumAvailableAmount: globalUnused,
            outstandingBalance: totalOutstandingBalance,
            facilityUtilization: common_1.utils.divideByOrZero(totalOutstandingBalance, globalCommitment),
            pendingRequests: params.pendingRequests.map((req) => ({
                ...req,
                fundingRequestComponentId: req.id,
                fundingRequestId,
            })),
            drawAvailability: globalUnused,
            recycleAvailability: 0,
            borrowingBaseReportMetadata: null,
            assignedEligibleExposureSinceLastApprovedFundingRequest: null,
            borrowingBaseReportMetadataForLastApprovedFundingRequest: null,
        },
        data: {
            resolvedLoan: {
                get: () => submittedLoans[0] ?? undefined,
            },
            loanType: {
                get: () => 'origination',
                set: () => {
                    return [];
                },
            },
            pricingOptions: {
                get: () => {
                    return loanMechanics.pricingOptions.map((option) => ({
                        key: option.key,
                        name: option.name,
                        lookbackDays: option.type === 'benchmark'
                            ? option.determination.lookback.days
                            : 0,
                        margin: option.margin.value,
                        invoiceMechanics: option.invoiceMechanics,
                    }));
                },
                set: (request) => {
                    const newPricingOption = loanMechanics.pricingOptions.find((option) => option.key === request.pricingOptionKey);
                    (0, common_1.finleyAssert)(newPricingOption, 'Invalid pricing option');
                    global.pricingOption = newPricingOption;
                    global.invoiceMechanicsKey = request.invoiceMechanicsKey;
                    // Reset draws
                    for (const drawRequest of drawRequests.values()) {
                        (0, common_1.finleyAssert)(drawRequest.loan, 'Loan must exist');
                        const updatedDrawRequest = (0, exports.getDrawRequest)({
                            drawRequest,
                            pricingOption: global.pricingOption,
                            loanMechanics,
                            benchmarkRates: fundingMechanics.benchmarkRates,
                            borrowerBankAccount,
                            lenderBankAccounts: fundingMechanics.bankAccounts,
                            fundingRequestComponents: currentFundingRequestComponents,
                            updateParams: {
                                amount: drawRequest.requestAmount,
                                externalLoanId: drawRequest.loan.externalLoanId ?? null,
                                maturityDate: drawRequest.loan.maturityDate,
                                targetFundingDate: drawRequest.targetFundingDate,
                            },
                        });
                        drawRequests.set(drawRequest.key, updatedDrawRequest);
                    }
                    return ['pricingOptions', 'drawRequests', 'maturityDate'];
                },
            },
            maturityDate: {
                get: () => {
                    return global.maturityDate;
                },
                set: (request) => {
                    global.maturityDate = request.date;
                    for (const drawRequest of drawRequests.values()) {
                        (0, common_1.finleyAssert)(drawRequest.loan, 'Loan must exist');
                        const updatedDrawRequest = (0, exports.getDrawRequest)({
                            drawRequest,
                            pricingOption: global.pricingOption,
                            loanMechanics,
                            benchmarkRates: fundingMechanics.benchmarkRates,
                            borrowerBankAccount,
                            lenderBankAccounts: fundingMechanics.bankAccounts,
                            fundingRequestComponents: currentFundingRequestComponents,
                            updateParams: {
                                amount: drawRequest.requestAmount,
                                externalLoanId: drawRequest.loan.externalLoanId ?? null,
                                maturityDate: global.maturityDate,
                                targetFundingDate: drawRequest.targetFundingDate,
                            },
                        });
                        drawRequests.set(drawRequest.key, updatedDrawRequest);
                    }
                    return ['maturityDate'];
                },
            },
            externalLoanId: {
                get: () => {
                    return global.externalLoanId ?? undefined;
                },
                set: (request) => {
                    global.externalLoanId = request.externalLoanId ?? null;
                    for (const drawRequest of drawRequests.values()) {
                        (0, common_1.finleyAssert)(drawRequest.loan, 'Loan must exist');
                        const updatedDrawRequest = (0, exports.getDrawRequest)({
                            drawRequest,
                            pricingOption: global.pricingOption,
                            loanMechanics,
                            benchmarkRates: fundingMechanics.benchmarkRates,
                            borrowerBankAccount,
                            lenderBankAccounts: fundingMechanics.bankAccounts,
                            fundingRequestComponents: currentFundingRequestComponents,
                            updateParams: {
                                amount: drawRequest.requestAmount,
                                externalLoanId: global.externalLoanId,
                                maturityDate: drawRequest.loan.maturityDate,
                                targetFundingDate: drawRequest.targetFundingDate,
                            },
                        });
                        drawRequests.set(drawRequest.key, updatedDrawRequest);
                    }
                    return ['externalLoanId'];
                },
            },
            recycleRequests: {
                isGlobalFundingDate: true,
                get: () => {
                    return [];
                },
                set: () => {
                    return [];
                },
            },
            netRecycleRequest: {
                get: () => {
                    return null;
                },
                set: () => {
                    return [];
                },
            },
            drawRequests: {
                isGlobalFundingDate: true,
                get: () => {
                    return expectedRequests.map(({ commitmentKey }) => {
                        const drawRequest = drawRequests.get(commitmentKey);
                        (0, common_1.finleyAssert)(drawRequest, 'Draw request must exist');
                        return drawRequest;
                    });
                },
                set: (request) => {
                    if (request.key) {
                        for (const drawRequest of drawRequests.values()) {
                            (0, common_1.finleyAssert)(drawRequest.loan, 'Loan must exist');
                            const updatedDrawRequest = (0, exports.getDrawRequest)({
                                drawRequest,
                                pricingOption: global.pricingOption,
                                loanMechanics,
                                benchmarkRates: fundingMechanics.benchmarkRates,
                                borrowerBankAccount,
                                lenderBankAccounts: fundingMechanics.bankAccounts,
                                fundingRequestComponents: currentFundingRequestComponents,
                                updateParams: {
                                    amount: request.key === drawRequest.key ? request.amount : 0,
                                    externalLoanId: drawRequest.loan.externalLoanId ?? null,
                                    maturityDate: drawRequest.loan.maturityDate,
                                    targetFundingDate: drawRequest.targetFundingDate,
                                },
                            });
                            drawRequests.set(drawRequest.key, updatedDrawRequest);
                        }
                    }
                    return [
                        'drawRequests',
                        'expectedSubmission',
                        'fundingImpact',
                        'recycleRequests',
                    ];
                },
            },
            targetFundingDate: {
                set: (request) => {
                    global.targetFundingDate = request.targetFundingDate;
                    for (const drawRequest of drawRequests.values()) {
                        (0, common_1.finleyAssert)(drawRequest.loan, 'Loan must exist');
                        const updatedDrawRequest = (0, exports.getDrawRequest)({
                            drawRequest,
                            pricingOption: global.pricingOption,
                            loanMechanics,
                            benchmarkRates: fundingMechanics.benchmarkRates,
                            borrowerBankAccount,
                            lenderBankAccounts: fundingMechanics.bankAccounts,
                            fundingRequestComponents: currentFundingRequestComponents,
                            updateParams: {
                                amount: drawRequest.requestAmount,
                                externalLoanId: drawRequest.loan.externalLoanId ?? null,
                                maturityDate: drawRequest.loan.maturityDate,
                                targetFundingDate: global.targetFundingDate,
                            },
                        });
                        drawRequests.set(drawRequest.key, updatedDrawRequest);
                    }
                    return ['drawRequests', 'expectedSubmission', 'fundingImpact'];
                },
            },
            fundingImpact: {
                get: () => {
                    return {
                        commitment: { amount: globalCommitment, currency: 'USD' },
                        borrowingBaseAmount: null,
                        maximumAvailableAmount: null,
                        outstandingBalance: {
                            before: totalOutstandingBalance,
                            after: totalOutstandingBalance +
                                calculateTotal(Array.from(drawRequests.values()).map((val) => val.requestAmount)),
                        },
                        drawAvailability: {
                            before: globalCommitment - totalOutstandingBalance,
                            after: globalCommitment -
                                (totalOutstandingBalance +
                                    calculateTotal(Array.from(drawRequests.values()).map((val) => val.requestAmount))),
                        },
                        recycleAvailability: null,
                    };
                },
            },
            expectedSubmission: {
                get: () => {
                    return {
                        recycle: {
                            global: {
                                requestAmount: 0,
                                constraints: [],
                                targetFundingDate: global.targetFundingDate, // unused
                            },
                            requests: [], // unused
                        },
                        draw: {
                            global: {
                                requestAmount: Array.from(drawRequests.values()).reduce((sum, { requestAmount }) => sum + requestAmount, 0),
                                constraints: [],
                                pricingOptionKey: global.pricingOption.key,
                                invoiceMechanicsKey: global.invoiceMechanicsKey,
                                targetFundingDate: global.targetFundingDate,
                            },
                            requests: expectedRequests
                                .map(({ commitmentKey }) => {
                                const drawRequest = drawRequests.get(commitmentKey);
                                (0, common_1.finleyAssert)(drawRequest, 'Draw request must exist');
                                (0, common_1.finleyAssert)(drawRequest.loan, 'Loan must exist');
                                return {
                                    key: drawRequest.key,
                                    name: drawRequest.name,
                                    amount: drawRequest.requestAmount,
                                    loanKey: drawRequest.loan?.key,
                                    pricingOptionKey: drawRequest.pricingOption.key,
                                    invoiceMechanicsKey: global.invoiceMechanicsKey,
                                    targetFundingDate: drawRequest.targetFundingDate,
                                    shares: drawRequest.loan.shares,
                                    externalLoanId: drawRequest.loan.externalLoanId,
                                    maturityDate: drawRequest.loan.maturityDate,
                                    loanType: 'origination',
                                };
                            })
                                .filter((request) => request.amount > 0),
                        },
                    };
                },
            },
        },
        submission: {
            getPayload: () => {
                // To unblock zero value draw submissions, a form input needs to be added to indicate this
                // Otherwise, all draws are already set to 0 and will generate loans by default
                const loanRequests = Array.from(drawRequests.values()).filter(({ requestAmount }) => requestAmount > 0);
                if (mode === 'upsize' && loanRequests.length > 1) {
                    return {
                        isValid: false,
                        payload: null,
                        errors: [
                            'Invalid draw request amount - only one draw request is allowed for upsize requests',
                        ],
                    };
                }
                // TODO: Revert check after supporting multiple submissions
                if (loanRequests.length !== 1) {
                    return {
                        isValid: false,
                        payload: null,
                        errors: [
                            'Invalid submission - only one draw request must be present.',
                        ],
                    };
                }
                if (!loanRequests.some(({ requestAmount }) => requestAmount >= 0)) {
                    return {
                        isValid: false,
                        payload: null,
                        errors: [
                            'Invalid submission - at least one non-zero draw request must be present.',
                        ],
                    };
                }
                // Payload for this initial launch is always scoped to a single loan
                const submission = {
                    isValid: true,
                    payload: {
                        apiVersion: 2,
                        id: currentFundingRequestComponents[0].fundingRequestId,
                        fundingRequestComponents: currentFundingRequestComponents
                            .filter((component) => component.type !== 'drawRequest')
                            .map((component) => ({
                            id: component.id,
                            type: component.type,
                            amount: 0,
                            sourceBankAccountId: component.sourceBankAccountId,
                            destinationBankAccountId: component.destinationBankAccountId,
                            targetFundingDate: common_1.date.currentDateStr(), // Immediate settlement, don't care
                        })),
                        loanRequests: [],
                    },
                };
                for (const loanRequest of loanRequests) {
                    (0, common_1.finleyAssert)(loanRequest.loan, 'Request must contain loan details');
                    submission.payload.loanRequests.push({
                        externalLoanId: loanRequest.loan.externalLoanId,
                        maturityDate: loanRequest.loan.maturityDate,
                        loanType: 'origination',
                        pricingOptionKey: loanRequest.pricingOption.key,
                        loanKey: loanRequest.loan.key,
                        commitmentKey: loanRequest.key,
                        invoiceMechanicsKey: global.invoiceMechanicsKey,
                    });
                    for (const share of loanRequest.loan.shares) {
                        const component = currentFundingRequestComponents.find(({ id }) => id === share.fundingRequestComponentId);
                        (0, common_1.finleyAssert)(component, 'Component must exist');
                        submission.payload.fundingRequestComponents.push({
                            id: component.id,
                            type: component.type,
                            amount: component.type === 'drawRequest' ? share.amount : 0,
                            sourceBankAccountId: component.sourceBankAccountId,
                            destinationBankAccountId: component.destinationBankAccountId,
                            targetFundingDate: loanRequest.targetFundingDate,
                        });
                    }
                }
                return submission;
            },
            getState: () => {
                // *** Validate Draw ***
                if (mode === 'upsize' && drawRequests.size > 1) {
                    return {
                        valid: false,
                        errors: [
                            'Invalid draw request amount - only one draw request is allowed for upsize requests',
                        ],
                    };
                }
                // Global
                const totalDraw = common_1.utils.jsRound({
                    value: lodash_1.default.sumBy(Array.from(drawRequests.values()), (opt) => opt.requestAmount),
                    precision: 2,
                });
                if (totalDraw > globalUnused) {
                    return {
                        valid: false,
                        errors: [
                            `Invalid draw request amount - maximum draw cannot exceed global unused commitment: $${globalUnused.toLocaleString()}`,
                        ],
                    };
                }
                const globalMinimum = Array.from(drawRequests.values())[0]?.constraints.minimum ?? 0;
                if (totalDraw > 0 && totalDraw < globalMinimum) {
                    return {
                        valid: false,
                        errors: [
                            `Invalid draw request amount - required to be either $0 or an amount greater than the minimum draw: $${globalMinimum.toLocaleString()}`,
                        ],
                    };
                }
                // Individual
                const individualDrawTests = Array.from(drawRequests.values()).every((drawRequest) => {
                    if (drawRequest.requestAmount < 0) {
                        return false;
                    }
                    if (drawRequest.requestAmount > 0 &&
                        drawRequest.requestAmount < drawRequest.constraints.minimum) {
                        return false;
                    }
                    if (drawRequest.requestAmount > 0 &&
                        drawRequest.requestAmount > drawRequest.constraints.maximum) {
                        return false;
                    }
                    // 0 here, true
                    return true;
                });
                if (!individualDrawTests) {
                    return {
                        valid: false,
                        errors: [
                            'Invalid draw request amounts. Please ensure that no negative values are present and also that maximum constraints are respected.',
                        ],
                    };
                }
                return { valid: true };
            },
        },
    };
};
exports.getFundingRequestGeneratorWithLoanMechanics = getFundingRequestGeneratorWithLoanMechanics;
//# sourceMappingURL=requestLoanMechanics.js.map